/* eslint-disable no-continue */
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { WebcamImage } from 'ngx-webcam';
import { Observable, Subject } from 'rxjs';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';

export interface UploadModalOptions {
    title: string;
    multiple: boolean;
    restrictionMessage: string;
    allowedFileTypes: string[];
    maxFileSize: number;
    autoComplete: boolean;
}

@Component({
    selector: 'app-upload-modal',
    templateUrl: './upload-modal.component.html',
    styleUrls: ['./upload-modal.component.scss'],
})
export class UploadModalComponent {
    @ViewChild('fileDropRef', { static: false }) fileDropEl: ElementRef;

    public files: File[] = [];

    public options: UploadModalOptions;

    public displayCamera = false;

    public cameraImageQuality = 1;

    public cameraImageCaptureType = 'image/jpeg';

    public displayWebcamOption = false;

    private cameraCapture: Subject<void> = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<UploadModalComponent>,
        private messageService: MessageService,
        private translateService: TranslateService,
        @Inject(MAT_DIALOG_DATA) options: UploadModalOptions,
    ) {
        this.options = {
            multiple: false,
            autoComplete: false,
            ...options,
        };
    }

    public async handleImageCapture(webcamImage: WebcamImage): Promise<void> {
        const blob = await (await fetch(webcamImage.imageAsDataUrl)).blob();
        const file = new File([blob], 'fileName.jpg', { type: 'image/jpeg' });
        this.prepareFilesList([file]);
    }

    public get cameraCaptureImageObservable(): Observable<void> {
        return this.cameraCapture.asObservable();
    }

    public triggerSnapshot(): void {
        this.cameraCapture.next();
    }

    public toggleDisplayCamera(): void {
        this.displayCamera = true;
    }

    onFileDropped($event): void {
        this.prepareFilesList($event);
    }

    fileBrowseHandler(files): void {
        this.prepareFilesList(files);
    }

    prepareFilesList(files: File[]): void {
        let preparingFiles = files;

        if (!this.options.multiple) {
            this.files = [];
        }

        if (preparingFiles.length > 1 && !this.options.multiple) {
            preparingFiles = [preparingFiles[0]];
        }

        for (const file of preparingFiles) {
            if (this.options.allowedFileTypes && !this.options.allowedFileTypes.includes(file.type)) {
                this.translateService
                    .get('common.uploadModal.fileWrongTypeError', {
                        name: file.name,
                    })
                    .subscribe((t) => {
                        this.messageService.warning(t);
                    });
                continue;
            }
            if (this.options.maxFileSize && file.size > this.options.maxFileSize) {
                this.translateService
                    .get('common.uploadModal.fileMaxSizeError', {
                        name: file.name,
                        limit: '20MB',
                    })
                    .subscribe((t) => {
                        this.messageService.warning(t);
                    });
                continue;
            }

            this.files = [...this.files, file];
        }

        if (this.options.autoComplete) {
            this.complete();
        }

        this.fileDropEl.nativeElement.value = '';
    }

    formatBytes(bytes, decimals = 2): string {
        if (bytes === 0) {
            return '0 Bytes';
        }
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
    }

    removeFile(index): void {
        this.files.splice(index, 1);
    }

    closeModal(): void {
        this.dialogRef.close();
    }

    complete(): void {
        this.dialogRef.close(this.files);
    }
}
